import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';

import Button from 'src/components/ui/Button';
import SavedSearchesIconButton, { type SavedSearchesIconButtonProps } from './SavedSearchesIconButton';
import TooltipButton from 'src/components/ui/TooltipButton';
import Icon from 'src/components/ui/Icon';
import { colours } from 'src/constants/colours';
import { SaveSearchModal } from './SaveSearchModal';
import { SavedSearchesModal } from './SavedSearchesModal';
import { useAdSearchState } from '../contexts/AdSearchStateContext';
import { removeExtraParamsFromAdsQueryVariables } from '../utils/removeExtraParamsFromAdsQueryVariables';

type Props = {
    isSaveSearchButtonDisabled: boolean;
    saveSearchButtonFullWith: boolean;
    onSaveSearchClick: () => void;
    onSavedSearchesClick: () => void;
    isSaveSearchModalOpen: boolean;
    handleCloseSaveSearchModal: () => void;
    isSavedSearchesModalOpen: boolean;
    handleSavedSearchesModalClose: () => void;
};

export const SearchCTAs: React.FC<Props> = ({
    isSaveSearchButtonDisabled,
    saveSearchButtonFullWith,
    onSaveSearchClick,
    onSavedSearchesClick,
    isSaveSearchModalOpen,
    handleCloseSaveSearchModal,
    isSavedSearchesModalOpen,
    handleSavedSearchesModalClose,
}) => {
    const { t } = useTranslation(['ad', 'common']);
    const { selectedSavedSearch, openSavedSearchTutorial, adsQueryVariables } = useAdSearchState();

    const queryVariablesWithoutExtraParams = removeExtraParamsFromAdsQueryVariables(adsQueryVariables);

    const savedSearchButtonProps = useMemo(
        () => ({
            onClick: onSavedSearchesClick,
            target: '_blank',
            size: 'medium',
            variant: 'outlined',
            sx: {
                p: '0.5rem',
                'min-width': 0,
                ml: '0.5rem',
                width: '2.5rem',
                height: '2.5rem',
                border: 'none',
                backgroundColor: openSavedSearchTutorial ? colours.white.main : colours.primary.O08,
                zIndex: openSavedSearchTutorial ? 1400 : 'initial',
                '&:hover': {
                    backgroundColor: openSavedSearchTutorial ? colours.white.O87 : colours.blackOpacity.O08,
                    border: 'none',
                },
            },
            shrink: true,
            openSavedSearchTutorial,
        }),
        [onSavedSearchesClick, openSavedSearchTutorial],
    );

    return (
        <Box display="flex" flexDirection="row" width="100%">
            <Button
                startIcon={
                    <Icon
                        style={{
                            width: '1rem',
                            height: '1rem',
                        }}
                        name="SavedSearches"
                        color={colours.white.main}
                    />
                }
                data-target="save-search"
                data-target-id="save-search"
                variant="contained"
                color="primary"
                onClick={onSaveSearchClick}
                fullWidth={saveSearchButtonFullWith}
                disabled={isSaveSearchButtonDisabled}
            >
                {selectedSavedSearch ? t('ad:updateSearch') : t('ad:saveMySearch')}
            </Button>
            {/* If openSavedSearchTutorial = true we have a popover on the button for the tutorial so don't add the tooltip */}
            {openSavedSearchTutorial ? (
                <SavedSearchesIconButton {...(savedSearchButtonProps as SavedSearchesIconButtonProps)} />
            ) : (
                <TooltipButton
                    title={t('common:tooltips.savedSearches')}
                    popperPropsOffset={[4, -10]}
                    ButtonComponent={SavedSearchesIconButton}
                    buttonProps={savedSearchButtonProps}
                />
            )}
            {isSaveSearchModalOpen && (
                <SaveSearchModal
                    open={isSaveSearchModalOpen}
                    onClose={handleCloseSaveSearchModal}
                    newParameters={{
                        ...queryVariablesWithoutExtraParams,
                        places: adsQueryVariables.places,
                        placesSource: adsQueryVariables.placesSource,
                    }}
                />
            )}
            {isSavedSearchesModalOpen && (
                <SavedSearchesModal open={isSavedSearchesModalOpen} onClose={handleSavedSearchesModalClose} />
            )}
        </Box>
    );
};
